import { isIterableObject } from "./types.js";
/**
 * Clone any JSON serializable value similar to JSON.parse(JSON.stringify()) but better performance
 */
export function deepClone(value) {
    if (!value) {
        return value;
    }
    if (Array.isArray(value)) {
        const { length } = value;
        const result = new Array(length);
        for (let i = 0; i < length; i++) {
            result[i] = deepClone(value[i]);
        }
        return result;
    }
    if (isIterableObject(value)) {
        const obj = value;
        const result = {};
        for (const key of Object.keys(obj)) {
            const value = obj[key];
            if (value !== undefined) {
                result[key] = deepClone(value);
            }
        }
        return result;
    }
    return value;
}
export const REMOVE = Symbol('REMOVE');
function arrayFilter(value) {
    return value !== REMOVE;
}
/**
 * Clone any JSON serializable value and transform using a callback
 * @param obj the value to clone
 * @param customizer a callback that is called for every key/index
 */
export function deepCloneWith(obj, customizer) {
    const clone = (value, key, parent) => {
        const newValue = customizer(value, key, parent, clone);
        if (newValue !== undefined) {
            return newValue;
        }
        if (Array.isArray(value)) {
            const { length } = value;
            const arr = new Array(length);
            let edited = false;
            for (let i = 0; i < length; i++) {
                const newValue = clone(value[i], i, value);
                if (newValue === REMOVE) {
                    edited = true;
                }
                arr[i] = newValue;
            }
            return edited ? arr.filter(arrayFilter) : arr;
        }
        if (isIterableObject(value)) {
            const newObject = {};
            for (const key of Object.keys(value)) {
                const newValue = clone(value[key], key, value);
                if (newValue !== undefined && newValue !== REMOVE) {
                    newObject[key] = newValue;
                }
            }
            return newObject;
        }
        return value;
    };
    return clone(obj, undefined, undefined);
}
/**
 * Clone any JSON serializable value and transform using a callback
 * @param value the value to clone
 * @param customizer a callback that is called for every key/index
 */
export function deepCloneWithPath(obj, customizer) {
    const clone = (value, key, parent, path) => {
        const customizedValue = customizer(value, key, parent, path);
        if (customizedValue === REMOVE) {
            return customizedValue;
        }
        const cloneValue = customizedValue ?? value;
        if (Array.isArray(cloneValue)) {
            const { length } = cloneValue;
            const arr = new Array(length);
            let edited = false;
            for (let i = 0; i < length; i++) {
                const newValue = clone(cloneValue[i], i, cloneValue, [...path, String(i)]);
                if (newValue === REMOVE) {
                    edited = true;
                }
                arr[i] = newValue;
            }
            return edited ? arr.filter(arrayFilter) : arr;
        }
        if (isIterableObject(cloneValue)) {
            const newObject = {};
            for (const key of Object.keys(cloneValue)) {
                const newValue = clone(cloneValue[key], key, cloneValue, [...path, key]);
                if (newValue !== undefined && newValue !== REMOVE) {
                    newObject[key] = newValue;
                }
            }
            return newObject;
        }
        return cloneValue;
    };
    return clone(obj, undefined, undefined, []);
}
